import React from 'react';
import { PropTypes as PT } from 'prop-types';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import _compact from 'lodash/compact';
import SVG from 'react-inlinesvg';
import manualDialIcon from '@lifesize/ux-assets/vectors/svgIcons/hashtag.svg';
import peopleIcon from '@lifesize/ux-assets/vectors/svgIcons/people.svg';
import otmIcon from '@lifesize/ux-assets/vectors/svgIcons/hourglass.svg';
import personIcon from '@lifesize/ux-assets/vectors/svgIcons/person.svg';
import roomSystemIcon from '@lifesize/ux-assets/vectors/svgIcons/room-system.svg';
import { nucleusConstants } from '@lifesize/nucleus';
import outgoingRing from 'assets/audio/outgoing-ring.mp3';
import busySignal from 'assets/audio/busy-signal.mp3';
import PulsingAvatar from 'components/PulsingAvatar/PulsingAvatar';
import Button from 'components/Button/Button';
import AudioElement from 'containers/AudioElement/AudioElementContainer';
import classes from './StartingCallModal.module.scss';

const iconType = {
  meeting: peopleIcon,
  oneTime: otmIcon,
  user: personIcon,
  roomSystem: roomSystemIcon,
  manual: manualDialIcon
};

export const callReasons = {
  busy: [
    nucleusConstants.CALL_STATE_DISCONNECTED, // 'disconnected';
    nucleusConstants.GALAXY_REASON_REJECTED, // 'rejected'
    nucleusConstants.GALAXY_REASON_BUSY, // 'busy'
    nucleusConstants.GALAXY_REASON_DO_NOT_DISTURB, // 'doNotDisturb'
    nucleusConstants.GALAXY_EVENT_PARTICIPANT_IGNORED, // 'participantIgnored'
    'CALLSTATEMODE_DISCONNECTED_BUSY', // ???
    'CALLSTATEMODE_DISCONNECTED_REJECT' // ???
  ],
  unreachable: [
    nucleusConstants.CALL_STATE_FAILED, // 'failed
    nucleusConstants.GALAXY_REASON_UNREACHABLE, // 'unreachable'
    nucleusConstants.GALAXY_REASON_TIMED_OUT, // 'timedOut'
    'CALLSTATEMODE_DISCONNECTED_UNREACHABLE' // ???
  ]
};

const getCallStatusMsg = (reason) => {
  if (callReasons.busy.includes(reason)) return 'busy';
  if (callReasons.unreachable.includes(reason)) return 'unreachable';
  return reason;
};

const messages = defineMessages({
  busy: {
    id: 'components.startCall.busy',
    defaultMessage: 'Busy'
  },
  unreachable: {
    id: 'components.startCall.unreachable',
    defaultMessage: 'Unreachable'
  },
  calling: {
    id: 'components.startCall.calling',
    defaultMessage: 'Calling...'
  },
  connecting: {
    id: 'components.startCall.connecting',
    defaultMessage: 'Connecting...'
  },
  connected: {
    id: 'components.startCall.connected',
    defaultMessage: 'Connected'
  },
  success: {
    id: 'components.startCall.success',
    defaultMessage: 'has been added!'
  },
  userNotFound: {
    id: 'components.startCall.userNotFound',
    defaultMessage: 'Sorry! The extension you dialed does not exist. Please try another number.'
  },
  noMedia: {
    id: 'components.startCall.noMedia',
    defaultMessage: 'Sorry, the connection failed. Refresh your page and try again.'
  }
});

function callErrorAudio() {
  return <AudioElement src={busySignal} autoPlay />;
}

const renderAvatar = ({ callStatus, callType, callMeetingType }) => {
  const animatePulse = ['calling', 'connecting'].includes(callStatus);
  let icon = iconType[callType];

  if (callMeetingType &&
    callMeetingType.length &&
    callMeetingType === nucleusConstants.MEETING_TYPE_ONE_TIME) {
    icon = iconType.oneTime;
  }

  if (animatePulse) {
    return <PulsingAvatar icon={icon} />;
  }

  return (
    <div className={`${classes.avatarContainer} ${classes.static}`}>
      <div className={classes.avatar}>
        <SVG src={icon} />
      </div>
    </div>
  );
};
let errorTimer = null;
const renderButtons = ({
  callId,
  callingError,
  cancelCall,
  redialCall,
  callDialString,
  drawerCall,
  isGuest,
  isCallDisconnected,
  callStatus
}) => {
  const buttonWidth = drawerCall ? 190 : 220;
  const cancelButton = () => {
    const cancelStyle = { minWidth: buttonWidth, width: buttonWidth };
    const cancelKind = drawerCall && callingError ? 'secondary' : 'primary';

    return (
      <Button
        kind={cancelKind}
        onClick={() => cancelCall(callId)}
        style={cancelStyle}
        dataAttrs={{ 'data-testid': 'cancel-call-btn' }}
      >
        <FormattedMessage
          id="components.startCallModal.cancel"
          defaultMessage="Cancel"
        />
      </Button>
    );
  };

  const tryAgainButton = () => (
    <Button
      style={{
        minWidth: buttonWidth,
        width: buttonWidth
      }}
      onClick={() => {
        clearTimeout(errorTimer);
        errorTimer = null;
        redialCall(callDialString);
      }}
      dataAttrs={{ 'data-testid': 'try-again-btn' }}
    >
      <FormattedMessage
        id="components.startCallModal.tryAgain"
        defaultMessage="Try again"
      />
    </Button>
  );

  const buttonToRender = () => {
    if (
      (drawerCall && callingError && callDialString) ||
      (!drawerCall &&
        callDialString &&
        (callingError || (isGuest && isCallDisconnected)))
    ) {
      return tryAgainButton();
    } else if (!drawerCall && callingError) {
      return callErrorAudio();
    } else if (callStatus === 'success') {
      return null;
    }
    return cancelButton();
  };

  return (
    <div className={classes.buttonBlock}>
      {buttonToRender()}
    </div>
  );
};

const renderCallTitle = (callTitle, callingError) => {
  if (callTitle) return callTitle;
  if (callingError) {
    return (
      <FormattedMessage
        id="components.startingCall.failure"
        defaultMessage="Unable to start call"
      />
    );
  }
  return (
    <FormattedMessage
      id="components.startingCall.title"
      defaultMessage="Starting your call"
    />
  );
};

const renderCallStatus = ({ callStatus, intl, callFailureReason }) => {
  const statusMsg = messages[getCallStatusMsg(callFailureReason)] || messages[getCallStatusMsg(callStatus)];
  return (statusMsg && intl) ? intl.formatMessage(statusMsg) : null;
};

let isMounted = false;
export class StartingCallModal extends React.Component {
  UNSAFE_componentWillMount() {
    isMounted = true;
    if (this.props.callingError) {
      errorTimer = setTimeout(() => {
        this.props.clearCall();
        errorTimer = null;
      }, this.props.callFailureTimeout);
    }
  }

  UNSAFE_componentWillUpdate(nextProps) {
    if (!this.props.callingError && nextProps.callingError) {
      errorTimer = setTimeout(() => {
        this.props.clearCall();
        errorTimer = null;
      }, this.props.callFailureTimeout);
    }
  }

  componentWillUnmount() {
    if (this.props.guestHasDisconnected) {
      this.props.navigateToTrialPage();
    }
    if (errorTimer) {
      clearTimeout(errorTimer);
      this.props.clearCall();
      errorTimer = null;
    }
    isMounted = false;
  }

  render() {
    const {
      callStatus,
      drawerCall,
      callType,
      callName,
      callingError,
      intl,
      callFailureReason,
      isCallDisconnected,
      cancelCall,
      redialCall,
      callDialString,
      isGuest,
      callTitle,
      callId,
      callMeetingType
    } = this.props;
    const callStatusClass = _compact([
      classes.callStatus,
      callingError ? classes.error : '',
      callStatus === 'success' || callStatus === 'connecting'
        ? classes.success
        : ''
    ]).join(' ');
    return (
      <div
        className={` ${classes.container} ${drawerCall ? classes.drawerCallContainer : ''}`}
      >
        {isMounted && callStatus === 'calling' && !drawerCall
          ? <AudioElement src={outgoingRing} autoPlay loop />
          : null}
        <header>
          <h1>
            {renderCallTitle(callTitle, callingError)}
          </h1>
        </header>
        {renderAvatar({ callStatus, callType, callMeetingType })}
        <div className={classes.callName}>
          {callName}
        </div>
        <div className={callStatusClass}>
          {renderCallStatus({
            callStatus,
            intl,
            callFailureReason
          })}
        </div>
        {renderButtons({
          callId,
          callingError,
          cancelCall,
          redialCall,
          callDialString,
          drawerCall,
          isGuest,
          isCallDisconnected,
          callStatus
        })}
      </div>
    );
  }
}

StartingCallModal.propTypes = {
  callId: PT.string,
  callingError: PT.bool,
  callName: PT.string,
  callStatus: PT.string,
  callDialString: PT.string,
  callType: PT.string,
  cancelCall: PT.func,
  clearCall: PT.func,
  intl: PT.object,
  redialCall: PT.func,
  drawerCall: PT.bool,
  callTitle: PT.string,
  callFailureReason: PT.string,
  isGuest: PT.bool,
  isCallDisconnected: PT.bool,
  callFailureTimeout: PT.number,
  guestHasDisconnected: PT.bool,
  navigateToTrialPage: PT.func,
  callMeetingType: PT.string
};

renderAvatar.propTypes = {
  callStatus: PT.string,
  callType: PT.string,
  callMeetingType: PT.string
};

renderButtons.propTypes = {
  callId: PT.string,
  callingError: PT.bool,
  cancelCall: PT.func,
  redialCall: PT.func,
  callDialString: PT.string,
  drawerCall: PT.bool,
  isGuest: PT.bool,
  isCallDisconnected: PT.bool,
  callStatus: PT.string
};

export default injectIntl(StartingCallModal);
